import { z } from "zod";
import { OrderStatusSchema, PillowShapeSchema } from "./enums.js";

// The customer-facing Order view (ADR-0051, issue #314): the wire contract of the
// single public `GET /orders/:token` endpoint. It is a **non-PII** projection —
// no login, no JWT — so a leaked capability link exposes nothing sensitive. It
// deliberately carries NO delivery destination (Paczkomat code / courier
// address), NO receiver name, NO phone, and NO email: the buyer already knows
// those, and they must never sit on a forwardable link. The `customer-order.test`
// pins the absence of any such field. Keyed on `orders.view_token`, distinct from
// the admin `AdminOrderDetail` (behind requireAdmin, which deliberately bundles
// the PII the operator needs to fulfil).

// One purchased line as the buyer sees it: the product shape (the frontend maps
// it to a localized product name — no server-side copy), the size + quantity, the
// snapshotted unit price, and a freshly-signed rendered-pillow thumbnail (null
// until the render exists). No project id, no internal ids — nothing the buyer
// can't already see on their keepsake.
export const CustomerOrderLine = z.object({
  // The pillow's shape drives the frontend's localized product name; a mixed order
  // (ADR-0054, #408) can carry a non-pillow line (a 3D print prices on colour depth,
  // not shape), so `shape` is nullable and the frontend falls back to the product type
  // (via a localized `productType` label — never a hardcoded string).
  shape: PillowShapeSchema.nullable(),
  productType: z.string().nullable(),
  sizeCode: z.string(),
  sizeCm: z.number().int().positive(),
  quantity: z.number().int().positive(),
  unitPriceCents: z.number().int().nonnegative(),
  thumbnailUrl: z.string().url().nullable(),
  // A stable, per-order group ordinal (0-based) the projection assigns by the line's
  // source project in first-appearance order, so the multi-line page can group lines
  // by pet (ADR-0054, #409) WITHOUT the raw project id crossing the wire. An ordinal
  // carries strictly less information than the UUID it stands in for, so grouping
  // stays non-PII — it names no pet, no buyer, nothing forwardable off the link.
  projectGroup: z.number().int().nonnegative(),
});
export type CustomerOrderLine = z.infer<typeof CustomerOrderLine>;

// One stop of the coarse 4-stop ladder as it crosses the wire. The Zod mirror of
// the shared `CustomerLadderStep` interface (./fulfillment) — the same
// interface-plus-Zod split as `FulfillmentStepState` vs `AdminFulfillmentStep`.
// `status` is one of the four ladder stops (paid → in_production → shipped →
// delivered); `at` is the mapped step's completion timestamp (ISO-8601) or null.
export const CustomerLadderStop = z.object({
  status: OrderStatusSchema,
  done: z.boolean(),
  current: z.boolean(),
  at: z.string().nullable(),
});
export type CustomerLadderStop = z.infer<typeof CustomerLadderStop>;

// The InPost tracking a buyer may follow once shipped — the parcel number + the
// carrier label. Shown by the page only when the order has reached `shipped` and
// a tracking number exists; the carrier deep-link is built on the frontend. This
// is the buyer's OWN parcel handle (not PII about anyone) — deliberately visible,
// unlike the delivery destination which is hidden.
export const CustomerOrderTracking = z.object({
  number: z.string(),
  carrier: z.string().nullable(),
});
export type CustomerOrderTracking = z.infer<typeof CustomerOrderTracking>;

export const CustomerOrderView = z.object({
  // `orders.id` — a stable reference the buyer can quote to support (the page
  // renders a short slice). Not PII; not a capability (the endpoint is keyed on
  // the unguessable `view_token`, never on this id).
  id: z.guid(),
  // The coarse status, derived by the SHARED `projectFulfillment` the admin
  // console uses (never the raw `orders.status` column) — operator and customer
  // can't disagree. `pending`/`cancelled`/`refunded` are rendered as their own
  // states by the page; the ladder covers only the four happy-path stops.
  status: OrderStatusSchema,
  ladder: z.array(CustomerLadderStop),
  createdAt: z.string(),
  currency: z.string(),
  // Amounts the buyer paid, split three ways (product + delivery + total). The
  // product subtotal is `total - shipping` by construction (checkout charges
  // exactly product + shipping), so it holds regardless of line count.
  productAmountCents: z.number().int().nonnegative(),
  shippingAmountCents: z.number().int().nonnegative(),
  totalAmountCents: z.number().int().nonnegative(),
  // EVERY order line (ADR-0054, #409) — a mixed order lists them all, grouped by
  // `projectGroup` on the page; never truncated to the first. Non-PII per line.
  lines: z.array(CustomerOrderLine),
  tracking: CustomerOrderTracking.nullable(),
});
export type CustomerOrderView = z.infer<typeof CustomerOrderView>;
